import { Button, HoverCard, Text } from '@mantine/core';
import { IconAlertCircleFilled, IconExclamationCircle, IconX } from '@tabler/icons-react';
import { useAtom, useAtomValue } from 'jotai';
import clsx from 'clsx';
import { stepIdToPrevOpOrderAtom } from '~/atoms/atoms';
import { debugModeAtom, erroredOperationAtom, selectedOperationIdAtom } from '~/atoms/no-code-atoms';
import { useLiveStateManager } from '~/hooks/useLiveStateManager';
import type { SelectedOperation } from '~/server/logic/prisma/operation-select';
import type { SelectedStep } from '~/server/logic/prisma/step-select';
import AIActionOp from './complex/AIActionOp';
import { CodeBlockOp } from './complex/impl/CodeBlock';
import OpRenderer from './simple/OpRenderer';
import AIAssertionOp from './complex/aiAssertion/AIAssertionOp';
import ComponentCallOp from './complex/component/ComponentCallOp';
import ConditionalOp from './complex/conditional/ConditionalOp';
import type { OperationParentContext } from '~/server/logic/steps/operation/types';

export default function OperationInner({
  operation,
  step,
  parentContexts,
  previewCursorAllowed,
  testContentId,
  readonly,
  disabled,
  disableMenu,
  editing,
  disableSelect,
  setEditing,
  stepIdx,
}: {
  operation: SelectedOperation;
  step: SelectedStep;
  // parentContext: see callback-handles.ts comment
  parentContexts?: OperationParentContext[];
  previewCursorAllowed: boolean;
  testContentId: string;
  readonly?: boolean;
  disabled?: boolean;
  disableMenu?: boolean;
  editing: boolean;
  disableSelect?: boolean;
  setEditing: (editing: boolean) => void;
  stepIdx: number;
}) {
  const stepIdToPrevOpOrder = useAtomValue(stepIdToPrevOpOrderAtom);
  const selectedOperationId = useAtomValue(selectedOperationIdAtom);
  const debugMode = useAtomValue(debugModeAtom);
  const { vmStatus, recorderState } = useLiveStateManager();

  const vmStatusOpMatch =
    vmStatus?.executionState?.operationId === operation.id ||
    vmStatus?.executionState?.stepSourceOperationId === operation.id;

  const operationReplaying = !!recorderState && vmStatus?.state === 'replaying' && vmStatusOpMatch;
  const selected = selectedOperationId === operation.id || operationReplaying;

  const [erroredOperation, setErroredOperation] = useAtom(erroredOperationAtom);

  const parentContext = parentContexts?.at(-1);

  return (
    <div className="w-full flex-grow">
      <div className="relative flex w-full flex-row items-center gap-2">
        {!!erroredOperation && erroredOperation?.id === operation.id ?
          <HoverCard
            width={320}
            shadow="md"
            withArrow
            openDelay={200}
            closeDelay={300}
          >
            <HoverCard.Target>
              <IconAlertCircleFilled
                size={18}
                className="cursor-pointer text-red-500 dark:text-red-400"
              />
            </HoverCard.Target>
            <HoverCard.Dropdown>
              <div className="space-y-3">
                <div className="flex items-center justify-between gap-2 text-red-500 dark:text-red-400">
                  <div className="flex items-center gap-2">
                    <IconExclamationCircle
                      size={16}
                      className="flex-shrink-0"
                    />
                    <Text size="sm">Action Error</Text>
                  </div>
                  <Button
                    size="xs"
                    variant="subtle"
                    color="red"
                    leftSection={<IconX size={12} />}
                    onClick={() => {
                      setErroredOperation(undefined);
                    }}
                  >
                    Clear
                  </Button>
                </div>
                <div className="rounded border-l-2 border-red-500 bg-red-50 p-3 dark:bg-red-950/20">
                  <Text
                    size="xs"
                    className="whitespace-pre-wrap font-mono"
                    c="red"
                  >
                    {erroredOperation?.errorText || 'An error occurred while executing this action'}
                  </Text>
                </div>
              </div>
            </HoverCard.Dropdown>
          </HoverCard>
        : null}
        {operation.type === 'CODE_BLOCK' ?
          <CodeBlockOp
            description={operation?.codeBlockOp?.description || undefined}
            code={operation?.codeBlockOp?.code || ''}
            step={step}
            previewCursorAllowed={previewCursorAllowed}
            readonly={readonly}
            operation={operation}
            disabled={disabled}
            order={
              operation?.order ||
              (stepIdToPrevOpOrder?.stepId === step.id ? stepIdToPrevOpOrder.prevOpOrder + 1 : undefined)
            }
            selected={selected}
            setEditing={setEditing}
            disableMenu={disableMenu}
            testContentId={testContentId}
            stepIdx={stepIdx}
            parentContexts={parentContexts}
          />
        : operation.type === 'AI_ASSERTION' ?
          <AIAssertionOp
            operation={operation}
            testContentId={testContentId}
            stepId={step.id}
            previewCursorAllowed={previewCursorAllowed}
            stepOrder={step.stepOrder}
            readonly={readonly}
            parentContext={parentContext}
          />
        : operation.type === 'AI_ACTION' ?
          <AIActionOp
            operation={operation}
            testContentId={testContentId}
            stepId={step.id}
            previewCursorAllowed={previewCursorAllowed}
            stepOrder={step.stepOrder}
            readonly={readonly}
            parentContext={parentContext}
          />
        : operation.type === 'COMPONENT_CALL' ?
          <ComponentCallOp
            operation={operation}
            testContentId={testContentId}
            step={step}
            previewCursorAllowed={previewCursorAllowed}
            readonly={readonly || operation.componentCallOp?.component?.archived}
            disableMenu={disableMenu}
            parentContexts={parentContexts}
          />
        : operation.type === 'CONDITIONAL' ?
          <ConditionalOp
            operation={operation}
            testContentId={testContentId}
            step={step}
            previewCursorAllowed={previewCursorAllowed}
            readonly={readonly}
            disableMenu={disableMenu}
            parentContexts={parentContexts}
          />
        : <div
            className={clsx(
              'relative w-full rounded-md py-1.5',
              !disableSelect &&
                (selected ? 'bg-gray-200 dark:bg-slate-700' : 'hover:bg-gray-100 dark:hover:bg-gray-800'),
            )}
          >
            <OpRenderer
              stepId={step.id}
              parentContexts={parentContexts}
              testContentId={testContentId}
              previewCursorAllowed={previewCursorAllowed}
              stepOrder={step.stepOrder}
              operation={operation}
              readonly={readonly}
              editing={editing}
              setEditing={setEditing}
              disableMenu={disableMenu}
              disableSelect={disableSelect}
              moveCursorEnabled={step.mode !== 'AI_DYNAMIC'}
              selected={selected}
            />
          </div>
        }
      </div>
      {!!debugMode && (
        <div className="ml-4 border-l border-orange-500 pl-2 font-mono text-xs">
          <div>Operation ID: {operation.id.slice(0, 8)}</div>
          <div>Operation Order: {operation.order}</div>
          <div>Step ID: {step.id.slice(0, 8)}</div>
          <div>Step Order: {step.stepOrder}</div>
          <div>
            Parent Contexts: <pre>{JSON.stringify(parentContexts ?? 'undefined', null, 2)}</pre>
          </div>
        </div>
      )}
    </div>
  );
}
